import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, BellRing, X, Mail, Loader2, Check } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { API } from '@/App';

const PriceAlertButton = ({ property }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  // Check if user already has price alert for this property
  useEffect(() => {
    const checkExisting = async () => {
      const savedEmail = localStorage.getItem('ikonic_price_alert_email');
      if (savedEmail && property?.id) {
        try {
          const res = await axios.get(`${API}/price-alerts/check?property_id=${property.id}&email=${encodeURIComponent(savedEmail)}`);
          setIsSubscribed(res.data.subscribed);
          setEmail(savedEmail);
        } catch (e) {
          // Ignore errors
        }
      }
    };
    checkExisting();
  }, [property?.id]);

  // Try to get email from newsletter subscription
  useEffect(() => {
    const savedEmail = localStorage.getItem('ikonic_newsletter_email') || 
                       localStorage.getItem('ikonic_price_alert_email');
    if (savedEmail) {
      setEmail(savedEmail);
    }
  }, []);

  const handleSubscribe = async () => {
    if (!email.trim() || !email.includes('@')) {
      toast.error(t('priceAlert.invalidEmail', 'Por favor ingresa un email válido'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/price-alerts/subscribe`, {
        property_id: property.id,
        property_name: property.name,
        current_price: property.price_usd,
        email: email.toLowerCase().trim()
      });

      // Save email for future use
      localStorage.setItem('ikonic_price_alert_email', email.toLowerCase().trim());
      
      setIsSubscribed(true);
      setShowModal(false);
      toast.success(t('priceAlert.subscribed', '¡Te notificaremos si baja el precio!'));
    } catch (error) {
      if (error.response?.status === 409) {
        setIsSubscribed(true);
        setShowModal(false);
        toast.info(t('priceAlert.alreadySubscribed', 'Ya tienes una alerta para esta propiedad'));
      } else {
        toast.error(t('priceAlert.error', 'Error al crear la alerta'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    setLoading(true);
    try {
      await axios.delete(`${API}/price-alerts/unsubscribe?property_id=${property.id}&email=${encodeURIComponent(email)}`);
      setIsSubscribed(false);
      toast.success(t('priceAlert.unsubscribed', 'Alerta de precio eliminada'));
    } catch (error) {
      toast.error(t('priceAlert.error', 'Error al eliminar la alerta'));
    } finally {
      setLoading(false);
    }
  };

  if (!property) return null;

  return (
    <>
      <Button
        onClick={() => isSubscribed ? handleUnsubscribe() : setShowModal(true)}
        variant="outline"
        disabled={loading}
        className={`w-full ${
          isSubscribed 
            ? 'border-green-500/50 text-green-400 hover:bg-green-500/10' 
            : 'border-white/20 text-white hover:bg-white/10'
        }`}
        data-testid="price-alert-btn"
      >
        {loading ? (
          <Loader2 size={18} className="mr-2 animate-spin" />
        ) : isSubscribed ? (
          <BellRing size={18} className="mr-2" />
        ) : (
          <Bell size={18} className="mr-2" />
        )}
        {isSubscribed 
          ? t('priceAlert.active', 'Alerta de precio activa')
          : t('priceAlert.button', 'Alertarme si baja el precio')
        }
      </Button>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#121212] border border-white/10 rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white text-lg font-medium flex items-center gap-2">
                <Bell size={20} className="text-[#D4AF37]" />
                {t('priceAlert.title', 'Alerta de Precio')}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-white/60 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="bg-[#1a1a1a] p-4 rounded-lg mb-4">
              <p className="text-white font-medium">{property.name}</p>
              <p className="text-[#D4AF37] text-2xl font-mono mt-1">
                ${property.price_usd?.toLocaleString()} USD
              </p>
            </div>

            <p className="text-[#A3A3A3] text-sm mb-4">
              {t('priceAlert.description', 'Te enviaremos un email cuando el precio de esta propiedad baje.')}
            </p>

            <div className="space-y-3">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" size={18} />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-[#1a1a1a] border-white/10 text-white"
                  placeholder="tu@email.com"
                  data-testid="price-alert-email"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  className="flex-1 border-white/10 text-white/60 hover:text-white"
                >
                  {t('common.cancel', 'Cancelar')}
                </Button>
                <Button
                  onClick={handleSubscribe}
                  disabled={loading}
                  className="flex-1 bg-[#D4AF37] text-black hover:bg-[#FFE182]"
                  data-testid="confirm-price-alert-btn"
                >
                  {loading ? (
                    <Loader2 size={18} className="mr-2 animate-spin" />
                  ) : (
                    <Check size={18} className="mr-2" />
                  )}
                  {t('priceAlert.subscribe', 'Activar alerta')}
                </Button>
              </div>
            </div>

            <p className="text-[#525252] text-xs mt-4 text-center">
              {t('priceAlert.privacy', 'Solo usaremos tu email para notificarte cambios de precio.')}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default PriceAlertButton;
